import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import Drawer from "components/drawerSlide/drawer";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";

import CreditBookCustomerTagBadge from "./CreditBookCustomerTagBadge";

import { TCustomerTag } from "redux/reducers/models/customer";

import { countActiveFilters, emptyFilters } from "./creditBooksFilterUtils";

import "./creditBooksFilterDrawer.scss";

const FILTER_OPTION_INITIAL_VISIBLE_COUNT = 3;

function getVisibleFilterOptions<T>(items: T[], showAll: boolean): { visibleItems: T[]; hiddenCount: number } {
  if (showAll || items.length <= FILTER_OPTION_INITIAL_VISIBLE_COUNT) {
    return { visibleItems: items, hiddenCount: 0 };
  }

  return {
    visibleItems: items.slice(0, FILTER_OPTION_INITIAL_VISIBLE_COUNT),
    hiddenCount: items.length - FILTER_OPTION_INITIAL_VISIBLE_COUNT,
  };
}

export interface ICreditBooksFilterValues {
  balance_min: string;
  balance_max: string;
  customer_type_ids: number[];
  tag_ids: number[];
}

export { emptyFilters as emptyDrawerFilters, countActiveFilters };

interface ICustomerTypeOption {
  id: number;
  title: string;
  full_title?: string;
}

interface IProps {
  open: boolean;
  appliedFilters: ICreditBooksFilterValues;
  customerTypes: ICustomerTypeOption[];
  customerTags: TCustomerTag[];
  onClose: () => void;
  onApply: (filters: ICreditBooksFilterValues) => void;
}

export default function CreditBooksFilterDrawer(props: IProps) {
  const { open, appliedFilters, customerTypes, customerTags, onClose, onApply } = props;
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState<ICreditBooksFilterValues>(emptyFilters());
  const [filterSearch, setFilterSearch] = useState("");
  const [showAllCustomerTypes, setShowAllCustomerTypes] = useState(false);
  const [showAllCustomerTags, setShowAllCustomerTags] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftFilters({ ...appliedFilters });
      setFilterSearch("");
      setShowAllCustomerTypes(false);
      setShowAllCustomerTags(false);
    }
  }, [open, appliedFilters]);

  useEffect(() => {
    setShowAllCustomerTypes(false);
    setShowAllCustomerTags(false);
  }, [filterSearch]);

  function handleApply(): void {
    onApply(draftFilters);
    onClose();
  }

  function handleClearAll(): void {
    setDraftFilters(emptyFilters());
    setFilterSearch("");
    setShowAllCustomerTypes(false);
    setShowAllCustomerTags(false);
  }

  function toggleCustomerType(typeId: number, checked: boolean): void {
    setDraftFilters(prev => {
      if (typeId === -1) {
        return { ...prev, customer_type_ids: [] };
      }

      const nextIds = checked
        ? [...prev.customer_type_ids, typeId]
        : prev.customer_type_ids.filter(id => id !== typeId);

      return { ...prev, customer_type_ids: nextIds };
    });
  }

  function toggleTag(tagId: number, checked: boolean): void {
    setDraftFilters(prev => {
      if (tagId === -1) {
        return { ...prev, tag_ids: [] };
      }

      const nextIds = checked ? [...prev.tag_ids, tagId] : prev.tag_ids.filter(id => id !== tagId);

      return { ...prev, tag_ids: nextIds };
    });
  }

  const activeFilterCount = countActiveFilters(draftFilters);
  const allCustomerTypesSelected = draftFilters.customer_type_ids.length === 0;
  const allTagsSelected = draftFilters.tag_ids.length === 0;
  const normalizedFilterSearch = filterSearch.trim().toLowerCase();

  const filteredCustomerTypes = useMemo(() => {
    if (!normalizedFilterSearch) {
      return customerTypes ?? [];
    }

    return (customerTypes ?? []).filter(type => {
      const label = (type.full_title ?? type.title ?? "").toLowerCase();
      return label.includes(normalizedFilterSearch);
    });
  }, [customerTypes, normalizedFilterSearch]);

  const filteredCustomerTags = useMemo(() => {
    if (!normalizedFilterSearch) {
      return customerTags ?? [];
    }

    return (customerTags ?? []).filter(tag => tag.label.toLowerCase().includes(normalizedFilterSearch));
  }, [customerTags, normalizedFilterSearch]);

  const { visibleItems: visibleCustomerTypes, hiddenCount: hiddenCustomerTypeCount } = useMemo(
    () => getVisibleFilterOptions(filteredCustomerTypes, showAllCustomerTypes),
    [filteredCustomerTypes, showAllCustomerTypes],
  );

  const { visibleItems: visibleCustomerTags, hiddenCount: hiddenCustomerTagCount } = useMemo(
    () => getVisibleFilterOptions(filteredCustomerTags, showAllCustomerTags),
    [filteredCustomerTags, showAllCustomerTags],
  );

  return (
    <Drawer
      className="credit-books-filter-drawer"
      open={open}
      title={t("Filter credit books by")}
      subtitle={t("Apply filters to table data.")}
      onClose={onClose}
      onOk={handleApply}
      okText={activeFilterCount > 0 ? t("Apply {{count}} filters", { count: activeFilterCount }) : t("Apply filters")}
      showCancel
      cancelText={t("Cancel")}
      cancelButtonType="text"
      secondaryActions={[{ label: t("Clear all"), action: handleClearAll, buttonType: "secondary" }]}
      backDropClose
    >
      <Drawer.Section>
        <Input
          id="credit-book-filter-search"
          labelHidden
          icon={faMagnifyingGlass}
          placeholder={t("Search by customer type or tag...")}
          value={filterSearch}
          onChange={event => setFilterSearch(event.target.value)}
        />
      </Drawer.Section>

      <Drawer.Section bordered>
        <p className="credit-books-filter-section-label">{t("Current balance")}</p>
        <div className="credit-books-filter-balance-row">
          <Input
            label={t("Min.")}
            type="number"
            prefix="$"
            value={draftFilters.balance_min}
            onChange={event => setDraftFilters(prev => ({ ...prev, balance_min: event.target.value }))}
          />
          <Input
            label={t("Max.")}
            type="number"
            prefix="$"
            value={draftFilters.balance_max}
            onChange={event => setDraftFilters(prev => ({ ...prev, balance_max: event.target.value }))}
          />
        </div>
      </Drawer.Section>

      <Drawer.Section bordered>
        <p className="credit-books-filter-section-label">{t("Customer type")}</p>
        <div className="credit-books-filter-checkbox-list">
          <Checkbox
            id="credit-book-filter-customer-type-all"
            size="small"
            fullWidth
            label={t("All")}
            checked={allCustomerTypesSelected}
            onChange={() => toggleCustomerType(-1, true)}
          />
          {visibleCustomerTypes.map(type => (
            <Checkbox
              key={type.id}
              id={`credit-book-filter-customer-type-${type.id}`}
              size="small"
              fullWidth
              label={type.full_title ?? type.title}
              checked={!allCustomerTypesSelected && draftFilters.customer_type_ids.includes(type.id)}
              onChange={event => toggleCustomerType(type.id, event.target.checked)}
            />
          ))}
          {hiddenCustomerTypeCount > 0 ? (
            <button
              type="button"
              className="credit-books-filter-show-more"
              onClick={() => setShowAllCustomerTypes(true)}
            >
              {t("Show {{count}} more", { count: hiddenCustomerTypeCount })}
            </button>
          ) : null}
          {normalizedFilterSearch && filteredCustomerTypes.length === 0 ? (
            <p className="credit-books-filter-empty-results">{t("No customer types found")}</p>
          ) : null}
        </div>
      </Drawer.Section>

      <Drawer.Section>
        <p className="credit-books-filter-section-label">{t("Customer tag")}</p>
        <div className="credit-books-filter-checkbox-list">
          <Checkbox
            id="credit-book-filter-tag-all"
            size="small"
            fullWidth
            label={t("All")}
            checked={allTagsSelected}
            onChange={() => toggleTag(-1, true)}
          />
          {visibleCustomerTags.map(tag => (
            <Checkbox
              key={tag.id}
              id={`credit-book-filter-tag-${tag.id}`}
              size="small"
              fullWidth
              label={<CreditBookCustomerTagBadge label={tag.label} colour={tag.colour} />}
              checked={!allTagsSelected && draftFilters.tag_ids.includes(tag.id)}
              onChange={event => toggleTag(tag.id, event.target.checked)}
            />
          ))}
          {hiddenCustomerTagCount > 0 ? (
            <button
              type="button"
              className="credit-books-filter-show-more"
              onClick={() => setShowAllCustomerTags(true)}
            >
              {t("Show {{count}} more", { count: hiddenCustomerTagCount })}
            </button>
          ) : null}
          {normalizedFilterSearch && filteredCustomerTags.length === 0 ? (
            <p className="credit-books-filter-empty-results">{t("No customer tags found")}</p>
          ) : null}
        </div>
      </Drawer.Section>
    </Drawer>
  );
}
